/**
 * How the advisor gets between screens: the hamburger, or a rail down the
 * left. And how wide that rail starts.
 *
 * ── Why this is a preference and not a rewrite ───────────────────────────
 *
 * Trey, 27 Aug 2026: "Can we deploy the side bar and let me test it and if i
 * dont like it, can we revert back?"
 *
 * Revert was the wrong shape for that question. Reverting means a redeploy to
 * change his mind, one nav at a time, and no way to compare the two while
 * actually working. A preference means the switch is his, live, mid-task —
 * and it is the same answer the product needs anyway: this is multi-tenant,
 * and an advisor on a 13" laptop and one on a 27" monitor do not want the
 * same 260 pixels spent the same way.
 *
 * ── The defaults, and who chose them ─────────────────────────────────────
 *
 * `topbar`, because it is what every existing advisor already has. A
 * preference that silently rearranges the app for everybody on deploy is not
 * a preference, it is a rewrite with a switch bolted on.
 *
 * `collapsed`, from Trey directly: "start narrow unless user has changed
 * their preferences in settings." The rail is 72px of icons until somebody
 * says otherwise, which keeps the seven-column trip table intact on a laptop.
 *
 * ── Shape copied from `lib/theme.js`, deliberately ───────────────────────
 *
 * Pure decisions here, storage in `lib/nav-store.js`, exactly as the theme
 * splits. Same `{v, at}` settings shape, same "the document IS the state"
 * reader. Two preferences that behave differently for no reason is how an app
 * gets hard to hold in your head.
 */

/** The two ways to navigate. */
export const NAV_MODES = Object.freeze(['topbar', 'sidebar'])

/** What every advisor has today, and what they keep until they choose. */
export const DEFAULT_NAV = 'topbar'

/** The two widths the rail has. */
export const RAIL_STATES = Object.freeze(['collapsed', 'expanded'])

/** Trey's call: narrow first. */
export const DEFAULT_RAIL = 'collapsed'

/** Settings keys. Namespaced `mbm_` like every other synced setting. */
export const NAV_KEY = 'mbm_nav'
export const RAIL_KEY = 'mbm_rail'

/** The attributes the stylesheet reads. */
export const NAV_ATTR = 'data-nav'
export const RAIL_ATTR = 'data-rail'

/**
 * Coerce anything into a nav mode this app can render.
 *
 * Falls back rather than throwing, and falls back to the CURRENT design: a
 * corrupt settings value must not silently move an advisor's whole menu.
 *
 * @param {unknown} value
 * @returns {'topbar'|'sidebar'}
 */
export function normalizeNav(value) {
  const name = String(value == null ? '' : value).trim().toLowerCase()
  return NAV_MODES.includes(name) ? /** @type {'topbar'|'sidebar'} */ (name) : DEFAULT_NAV
}

/**
 * @param {unknown} value
 * @returns {'collapsed'|'expanded'}
 */
export function normalizeRail(value) {
  const name = String(value == null ? '' : value).trim().toLowerCase()
  return RAIL_STATES.includes(name) ? /** @type {'collapsed'|'expanded'} */ (name) : DEFAULT_RAIL
}

/** @param {'topbar'|'sidebar'} nav */
export function otherNav(nav) {
  return normalizeNav(nav) === 'sidebar' ? 'topbar' : 'sidebar'
}

/** @param {'collapsed'|'expanded'} rail */
export function otherRail(rail) {
  return normalizeRail(rail) === 'expanded' ? 'collapsed' : 'expanded'
}

/** What the Settings cards say. */
export function navLabel(nav) {
  return normalizeNav(nav) === 'sidebar' ? 'Sidebar' : 'Top menu'
}

export function navDescription(nav) {
  return normalizeNav(nav) === 'sidebar'
    ? 'Every screen down the left'
    : 'Everything behind the ☰ button'
}

export function railLabel(rail) {
  return normalizeRail(rail) === 'expanded' ? 'Wide' : 'Narrow'
}

export function railDescription(rail) {
  return normalizeRail(rail) === 'expanded'
    ? 'Icons and names, always readable'
    : 'Icons only — names on hover'
}

/**
 * Pull one setting out of the shared document, in priority order.
 *
 * The document stores `{v, at}`; a bare value is accepted too, because a
 * hand-edited settings document should work rather than mysteriously revert.
 *
 * @param {any} settings
 * @param {string} key
 * @returns {string}
 */
function fromSettings(settings, key) {
  const wrapped = settings && settings[key]
  const value = wrapped && typeof wrapped === 'object' ? wrapped.v : wrapped
  return value == null ? '' : String(value).trim()
}

/**
 * Decide both, from every source.
 *
 * @param {{settings?: any, localNav?: unknown, localRail?: unknown}} sources
 * @returns {{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}}
 */
export function resolveNav(sources = {}) {
  const cloudNav = fromSettings(sources.settings, NAV_KEY)
  const cloudRail = fromSettings(sources.settings, RAIL_KEY)
  const localNav = sources.localNav == null ? '' : String(sources.localNav).trim()
  const localRail = sources.localRail == null ? '' : String(sources.localRail).trim()
  return {
    nav: normalizeNav(cloudNav || localNav || DEFAULT_NAV),
    rail: normalizeRail(cloudRail || localRail || DEFAULT_RAIL),
  }
}

/**
 * Put both on the page. The only DOM this module touches.
 *
 * Attributes rather than classes, and on `<body>` AND `<html>`, for the
 * reason `theme-slate.css` records at length: a custom property declared on
 * `:root` resolves there, so a token the rail needs to re-point has to be
 * reachable from the element the tokens are declared on. Idempotent, and safe
 * before the body exists.
 *
 * @param {unknown} nav
 * @param {unknown} rail
 * @param {Document} [doc]
 * @returns {{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}}
 */
export function applyNav(nav, rail, doc = document) {
  const chosen = { nav: normalizeNav(nav), rail: normalizeRail(rail) }
  for (const el of [doc.body, doc.documentElement].filter(Boolean)) {
    el.setAttribute(NAV_ATTR, chosen.nav)
    el.setAttribute(RAIL_ATTR, chosen.rail)
  }
  return chosen
}

/**
 * What the page is currently wearing.
 *
 * Read from the document rather than held in a variable, for the reason
 * `lib/theme.js#currentTheme` gives: `applyNav` is the only writer, so the
 * attribute cannot get out of step with what the advisor is looking at. Three
 * controls will read this — Settings, the rail's own collapse arrow, and the
 * shell — and none of them may hold its own copy.
 *
 * @param {Document} [doc]
 * @returns {{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}}
 */
export function currentNav(doc = document) {
  const el = (doc && doc.body) || (doc && doc.documentElement)
  return {
    nav: normalizeNav(el && el.getAttribute(NAV_ATTR)),
    rail: normalizeRail(el && el.getAttribute(RAIL_ATTR)),
  }
}
